import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, concatMap, exhaustMap, map, mapTo, switchMap, withLatestFrom } from 'rxjs/operators';
import { Location } from '../../../models/Location';
import { ProfileMap } from '../../../models/ProfileMap';
import { ProfilePicture } from '../../../models/ProfilePicture';
import { User } from '../../../models/User';
import { KioskModeService } from '../../../services/kiosk-mode.service';
import { UserService } from '../../../services/user.service';
import * as accountsActions from '../../accounts/actions/accounts.actions';
import * as userActions from '../actions';

@Injectable()
export class UserEffects {
	getUser$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(userActions.getUser),
			concatMap(() => {
				return this.userService.getUser().pipe(
					map((user: User) => {
						return userActions.getUserSuccess({ user });
					}),
					catchError((error) => of(userActions.getUserFailure({ errorMessage: error.message })))
				);
			})
		);
	});

	getUserSuccess$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(userActions.getUserSuccess),
			switchMap((user: any) => {
				if (user.user?.extras?.dedicated_kiosk_location) {
					const kioskRoom = Location.fromJSON(user.user?.extras?.dedicated_kiosk_location);
					this.kioskMode.setTempRoom(kioskRoom);
					this.router.navigate(['main/kioskMode/settings']);
				}
				return [userActions.redirectUserToKioskSuccess()];
			})
		);
	});

	updateUser$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(userActions.updateUserAction),
			concatMap((action: any) => {
				return this.userService.updateUser(action.user.id, action.data).pipe(
					withLatestFrom(this.userService.userJSON$),
					map(([user, currentUser]: [User, User]) => {
						if (action.data.pin) {
							return userActions.updateUserPin({ pin: action.data.pin });
						}
						if (+user.id === +currentUser.id) {
							return userActions.updateUserSuccess({ user });
						} else {
							return accountsActions.updateAccounts({ account: user });
						}
					}),
					catchError((error) => of(userActions.updateUserFailure({ errorMessage: error.message })))
				);
			})
		);
	});

	updateUserPin$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(userActions.updateUserPin),
			map((action) => {
				return userActions.updateUserPinSuccess({ pin: action.pin });
			})
		);
	});

	updateProfilePicture$ = createEffect(() => {
		return this.actions$.pipe(
			ofType(userActions.updateUserPicture),
			exhaustMap((action) => {
				return this.userService.bulkAddProfilePictures([action.file]).pipe(
					switchMap((images: ProfilePicture[]) => {
						return this.userService.setProfilePictureToGoogle(images[0].upload_url, action.file, images[0].content_type).pipe(mapTo(images[0]));
					}),
					switchMap((image) => {
						return this.userService.uploadProfilePictures([+image.id], [action.user.id]);
					}),
					map(({ attached_photos }: { attached_photos: ProfileMap[] }) => {
						return { ...action.user, profile_picture: attached_photos[0].photo_url };
					}),
					map((user: User) => userActions.updateUserPictureSuccess({ user })),
					catchError((error) => of(userActions.updateUserPictureFailure({ errorMessage: error.message })))
				);
			})
		);
	});

	constructor(private actions$: Actions, private userService: UserService, private kioskMode: KioskModeService, private router: Router) {}
}
